import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import Icon from '~Scenes/SelfServiceKiosks/Components/Icon';
import receiptIcon from '../../Assets/Icons/receipt.svg';
import treeIcon from '../../Assets/Icons/treeIcon.svg';
import { useDispatch, useI18n } from '~Hooks';
import * as actions from '~actions';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';
import { useStorage } from '~Components/Storage';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 11;
  width: 100%;
  height: 100%;
  padding: 264px 120px 165px 120px;
  box-sizing: border-box;

  @media (orientation: landscape) {
    padding: 148px 67px 92px 67px;
    justify-content: center;
  }
`;

const Title = styled.h2`
  margin: 0 0 40px 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 80px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 72px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 68px;
    line-height: 61px;
  }
`;

const Description = styled.p`
  margin: 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-Regular;
  font-size: 48px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 40px;
    line-height: 47px;
  }
`;

const Buttons = styled.div`
  display: flex;
  padding: 150px 0 0 0;

  @media (orientation: landscape) {
    padding: 127px 0 0 0;
  }
`;

const Button = styled(TouchedWrapper)<{ background: string }>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  border-radius: 80px;
  height: 224px;
  min-width: 300px;
  margin: 0 16px;
  padding: 0 30px;
  transition: box-shadow 0.2s, transform 0.2s, opacity 0.2s;
  background: ${({ background }) => background};

  @media (orientation: landscape) {
    border-radius: 68px;
    height: 190px;
    min-width: 255px;
  }
`;

const ButtonLabel = styled.div<{ light?: boolean }>`
  color: ${({ light }) => (light ? '#fff' : 'rgb(19, 19, 22)')};
  font-family: Eina01-SemiBold;
  font-size: 48px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;
  padding: 8px 0 0 0;

  @media (orientation: landscape) {
    font-size: 40px;
    line-height: 47px;
  }
`;

type Props = {
  setReceiptOption: (choice: string) => void;
  orderId: string;
};

const Receipt: FC<Props> = ({ setReceiptOption, orderId }: Props) => {
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const useAdyen = storage.adyenTerminals.length > 0;
  const [paymentStarted, setPaymentStarted] = useState<boolean>(false);

  const startPayment = (choice: string) => {
    // Prevent multiple concurrent payment starts (#2nd-line issue where the Express crashed if the user pressed both buttons at the same time)
    if (paymentStarted) {
      return;
    }

    setPaymentStarted(true);
    setReceiptOption(choice);

    dispatch(actions.updateOrder(orderId, { noReceipt: choice === 'no' }));

    dispatch(
      // @ts-ignore
      actions.initiatePayment(orderId, {
        method: useAdyen ? 'ADYEN' : 'IZETTLE',
        canCancel: true,
      }),
    );
  };

  useEffect(() => {
    if (storage.alwaysPrintReceiptEPOS) {
      /** Prevents redux order being unpopulated */
      setTimeout(() => startPayment('yes'), 100);
    }
  }, [storage.alwaysPrintReceiptEPOS]);

  if (storage.alwaysPrintReceiptEPOS) {
    return null;
  }

  return (
    <Wrapper>
      <Title>{i18n('SSK.Receipt.Title')}</Title>
      <Description>{i18n('SSK.Receipt.Description')}</Description>
      <Buttons>
        <Button
          onClick={() => startPayment('yes')}
          disabled={paymentStarted}
          shadows={{
            pressed: '0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
            init:
              '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
          }}
          background="var(--colorWhite)">
          <Icon icon={receiptIcon} color={'#757578'} width={32} height={50} />
          <ButtonLabel>{i18n('SSK.Receipt.Yes')}</ButtonLabel>
        </Button>

        <Button
          onClick={() => startPayment('no')}
          disabled={paymentStarted}
          shadows={{
            pressed: '0px 11px 15px -7px rgba(0, 0, 0, 0.32)',
            init: `0px 11px 15px -7px rgba(0, 0, 0, 0.32), 0px 9px 46px 8px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.24)`,
          }}
          background="rgb(32, 153, 70)">
          <Icon icon={treeIcon} color="var(--colorWhite)" width={47} height={50} />
          <ButtonLabel light>{i18n('SSK.Receipt.No')}</ButtonLabel>
        </Button>
      </Buttons>
    </Wrapper>
  );
};

export default Receipt;
